import { createSlice } from '@reduxjs/toolkit';
import { EPISODE_LANDING_STEP } from '../../pages/AddMedia/TVShowSeasonEpisode/episodeSteps';
// import { extendedApiSlice } from '../services/movieServerApi'
export interface AddTvShowSeasonEpisodeState {
  currentTvShowSeasonEpisodeStep: number | null;
  selectedTvShowSeasonResult: any;
  tvShowSeason: number | null;
  selectedAddType: string | null;
  selectedDataSource: string | null;
  selectedItunesResult: any;
  selectedImdbResult: any;
  selectedItunesCountry: string | null;
  generalTvShowSeasonEpisodeData: any;
  secondaryTvShowData: any;
  castCrewMovieData: any;
  imagesTvShowSeasonEpisodeData: any;
  languagesTvShowSeasonEpisodeData: any;
  chaptersMovieData: any;
  finalTvShowData: any;
  generalTvShowSeasonData: any;
  castCrewTvShowSeasonEpisodeData: any;
  finalTvShowSeasonEpisodeData: any;
  tvShowSeasonEpisodesData: any;
  studios: any[];
  ratings: any[];
  qualities: any[];
  formats: any[];
  sources: any[];
  genres: any[];
  networks: any[];
  countries: any[];
  tags: any[];
  collections: any[];
  languages: any[];
  accessLevels: any[];
  newTvShowSeasonEpisodeId: string;
  suggestedShowTitle: string;
  newTvShowSeasonEpisodeUlid: string;
  // One language per sound track, in file order, or null for a
  // track nobody named. Answered on the languages step and carried
  // to the last one, which queues the muxing that needs it - see
  // the movie slice's identical field.
  audioTrackLanguages: (string | null)[] | null;
  // Positions of the sound tracks to drop from the file, in the
  // same order. Removed while the file is written; the original
  // is not kept, which is why the step says so plainly.
  removeAudioTracks: number[];
  mainAudioTrack: number | null;
  selectedTvShowSeasonEpisode: string;
  /**
   * The number and title IMDb's season page gave for the episode
   * that was picked. Kept because OMDb has no record of some
   * episodes at all, and then this is the only thing that knows
   * which number in the season it is - which cannot be guessed.
   */
  pickedEpisode: { number: string; title: string } | null;
  newEpisodeBackground: string;
  newEpisodeBackgroundVerticalPosition: number;
  newEpisodeFilePath: string;
  // True while the wizard is being walked to queue episodes rather than
  // to add one now: the only screen that behaves differently is the
  // episode picker, which becomes multi-select.
  queueMode: boolean;
  // Set only when the wizard was resumed from the queue screen, so the
  // finished job can be cleared once its episode actually exists.
  queuedJobId: string | null;
}

const initialState: AddTvShowSeasonEpisodeState = {
  // The wizard now opens on the landing screen that offers New,
  // Submit to Queue and Queued; New goes on to step 1 (the season
  // search) exactly as before.
  currentTvShowSeasonEpisodeStep: EPISODE_LANDING_STEP,
  tvShowSeason: null,
  selectedTvShowSeasonResult: null,
  selectedAddType: null,
  selectedDataSource: null,
  selectedItunesResult: null,
  selectedImdbResult: null,
  selectedItunesCountry: null,
  generalTvShowSeasonEpisodeData: null,
  secondaryTvShowData: null,
  castCrewMovieData: null,
  imagesTvShowSeasonEpisodeData: null,
  languagesTvShowSeasonEpisodeData: null,
  chaptersMovieData: { hasNamedChapters: false },
  finalTvShowData: {},
  tvShowSeasonEpisodesData: null,
  generalTvShowSeasonData: null,
  castCrewTvShowSeasonEpisodeData: null,
  finalTvShowSeasonEpisodeData: null,
  studios: [],
  ratings: [],
  qualities: [],
  formats: [],
  networks: [],
  sources: [],
  genres: [],
  countries: [],
  tags: [],
  collections: [],
  languages: [],
  accessLevels: [],
  newTvShowSeasonEpisodeId: '',
  suggestedShowTitle: '',
  newTvShowSeasonEpisodeUlid: '',
  audioTrackLanguages: null,
  removeAudioTracks: [],
  mainAudioTrack: null,
  selectedTvShowSeasonEpisode: '',
  pickedEpisode: null,
  newEpisodeBackground: '',
  newEpisodeBackgroundVerticalPosition: 0,
  newEpisodeFilePath: '',
  queueMode: false,
  queuedJobId: null,
};

export const addTvShowSeasonEpisodeSlice = createSlice({
  name: 'addTvShowSeasonEpisode',
  initialState,
  reducers: {
    setSelectedTvShowSeasonResult: (state, { payload }) => {
      state.selectedTvShowSeasonResult = payload;
    },
    setTvShowSeason: (state, { payload }) => {
      state.tvShowSeason = payload.tvShowSeason;
    },
    // Carried from New Files into the episode wizard's search. The
    // show's name, not the episode's: that wizard looks for a season
    // first, so the show is what it needs.
    setSuggestedShowTitle: (state, { payload }) => {
      state.suggestedShowTitle = payload;
    },
    setCurrentTvShowSeasonEpisodeStep: (state, { payload }) => {
      state.currentTvShowSeasonEpisodeStep = payload;
    },
    setTvShowSeasonEpisodesData: (state, { payload }) => {
      state.tvShowSeasonEpisodesData = payload;
    },
    setSelectedAddType: (state, { payload }) => {
      state.selectedAddType = payload;
    },
    setSelectedDataSource: (state, { payload }) => {
      state.selectedDataSource = payload;
    },
    setGeneralTvShowSeasonEpisodeData: (state, { payload }) => {
      state.generalTvShowSeasonEpisodeData = payload;
    },
    setCastCrewTvShowSeasonEpisodeData: (state, { payload }) => {
      state.castCrewTvShowSeasonEpisodeData = payload;
    },
    setImagesTvShowSeasonEpisodeData: (state, { payload }) => {
      state.imagesTvShowSeasonEpisodeData = payload;
    },
    setLanguagesTvShowSeasonEpisodeData: (state, { payload }) => {
      state.languagesTvShowSeasonEpisodeData = payload;
    },
    setFinalTvShowData: (state, { payload }) => {
      state.finalTvShowData = payload;
    },
    setNewTvShowSeasonEpisodeId: (state, { payload }) => {
      state.newTvShowSeasonEpisodeId = payload;
    },
    setNewTvShowSeasonEpisodeUlid: (state, { payload }) => {
      state.newTvShowSeasonEpisodeUlid = payload;
    },
    setPickedEpisode: (state, { payload }) => {
      state.pickedEpisode = payload;
    },
    setEpisodeAudioTrackLanguages: (state, { payload }) => {
      state.audioTrackLanguages = payload;
    },
    setEpisodeRemoveAudioTracks: (state, { payload }) => {
      state.removeAudioTracks = payload;
    },
    setEpisodeMainAudioTrack: (state, { payload }) => {
      state.mainAudioTrack = payload;
    },

    setNewEpisodeFilePath: (state, { payload }) => {
      state.newEpisodeFilePath = payload;
    },
    setQueueMode: (state, { payload }) => {
      state.queueMode = payload;
    },
    setQueuedJobId: (state, { payload }) => {
      state.queuedJobId = payload;
    },

    setSelectedTvShowSeasonEpisode: (state, { payload }) => {
      state.selectedTvShowSeasonEpisode = payload;
    },

    setSelectedTvShowSeasonEpisodeData: (state, { payload }) => {
      state.generalTvShowSeasonEpisodeData =
        payload.generalTvShowSeasonEpisodeData;
      state.castCrewTvShowSeasonEpisodeData =
        payload.castCrewTvShowSeasonEpisodeData;
      state.languagesTvShowSeasonEpisodeData =
        payload.languagesTvShowSeasonEpisodeData;
      state.studios = payload.studios;
      state.ratings = payload.ratings;
      state.qualities = payload.qualities;
      state.formats = payload.formats;
      state.sources = payload.sources;

      state.genres = payload.genres;
      state.countries = payload.countries;
      state.tags = payload.tags;
      state.collections = payload.collections;

      state.languages = payload.languages;
      state.accessLevels = payload.accessLevels;
    },

    setFinalTvShowSeasonEpisodeData: (state, { payload }) => {
      state.finalTvShowSeasonEpisodeData = payload;
    },
    resetEpisodeState: () => initialState,
    setNewEpisodeBackground: (state, { payload }) => {
      state.newEpisodeBackground = payload.episodeBackground;
    },
    setNewEpisodeBackgroundVerticalPosition: (state, { payload }) => {
      state.newEpisodeBackgroundVerticalPosition = payload;
    },
  },
});

export const {
  setSelectedTvShowSeasonResult,
  setCurrentTvShowSeasonEpisodeStep,
  setSuggestedShowTitle,
  setSelectedAddType,
  setSelectedDataSource,
  setCastCrewTvShowSeasonEpisodeData,
  setImagesTvShowSeasonEpisodeData,
  setLanguagesTvShowSeasonEpisodeData,
  setFinalTvShowData,
  setNewTvShowSeasonEpisodeId,
  setNewTvShowSeasonEpisodeUlid,
  setPickedEpisode,
  setEpisodeAudioTrackLanguages,
  setEpisodeRemoveAudioTracks,
  setEpisodeMainAudioTrack,
  setTvShowSeason,
  setTvShowSeasonEpisodesData,
  setSelectedTvShowSeasonEpisode,
  setSelectedTvShowSeasonEpisodeData,
  setGeneralTvShowSeasonEpisodeData,
  setFinalTvShowSeasonEpisodeData,
  resetEpisodeState,
  setQueueMode,
  setQueuedJobId,
  setNewEpisodeBackground,
  setNewEpisodeBackgroundVerticalPosition,
  setNewEpisodeFilePath,
} = addTvShowSeasonEpisodeSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

export default addTvShowSeasonEpisodeSlice.reducer;
